'use client'

import { useState } from 'react'

import { EyeClosed, Eye } from 'lucide-react'

import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { Combobox } from '@/shared/form/Combobox'
import { FormCheck } from '@/shared/form/FormCheck'
import { FormItem } from '@/shared/form/FormItem'
import { Input } from '@/shared/form/Input'
import { useSelectOptions } from '@/shared/form/Select/useSelectOptions'
import { AppLink } from '@/shared/lib/router'
import { authInputClassName as inputClassName } from '../../../constants/styles'

export function SignUpFields() {
  const { config } = useAuthContext()
  const [seePassword, setSeePassword] = useState(false)
  const [seePasswordConfirmation, setSeePasswordConfirmation] = useState(false)

  const withEmailConfirmation = !!config?.signUp?.withEmailConfirmation

  // school_year is a small, fixed enum (13 values) — fetched once from the
  // API (source of truth) and searched client-side via Combobox's local
  // filter, rather than wired as `async` (which would forward the typed
  // search to `/public/school_years`, an endpoint that ignores it).
  const { options: schoolYearOptions } = useSelectOptions(
    undefined,
    { url: '/public/school_years', labelKey: 'label', valueKey: 'value' },
    ''
  )

  return (
    <>
      <FormItem name="name" vertical>
        <FormItem.Label>Nome completo</FormItem.Label>
        <FormItem.Control>
          <Input name="name" autoComplete="name" className={inputClassName} />
        </FormItem.Control>
      </FormItem>

      <FormItem name="email" vertical>
        <FormItem.Label>E-mail</FormItem.Label>
        <FormItem.Control>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            className={inputClassName}
          />
        </FormItem.Control>
      </FormItem>

      {withEmailConfirmation && (
        <FormItem name="email_confirmation" vertical>
          <FormItem.Label>Confirmação de e-mail</FormItem.Label>
          <FormItem.Control>
            <Input
              name="email_confirmation"
              type="email"
              autoComplete="email"
              className={inputClassName}
            />
          </FormItem.Control>
        </FormItem>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <FormItem name="password" vertical>
          <FormItem.Label>Senha</FormItem.Label>
          <div className="relative">
            <FormItem.Control>
              <Input
                name="password"
                type={seePassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClassName}
              />
            </FormItem.Control>
            <button
              type="button"
              className="absolute right-0 flex items-center pr-3 -translate-y-1/2 top-1/2 gap-x-1"
              onClick={() => setSeePassword(!seePassword)}
              tabIndex={-1}
            >
              {seePassword ? (
                <Eye className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeClosed className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </FormItem>

        <FormItem name="password_confirmation" vertical>
          <FormItem.Label>Confirme sua senha</FormItem.Label>
          <div className="relative">
            <FormItem.Control>
              <Input
                name="password_confirmation"
                type={seePasswordConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClassName}
              />
            </FormItem.Control>
            <button
              type="button"
              className="absolute right-0 flex items-center pr-3 -translate-y-1/2 top-1/2 gap-x-1"
              onClick={() =>
                setSeePasswordConfirmation(!seePasswordConfirmation)
              }
              tabIndex={-1}
            >
              {seePasswordConfirmation ? (
                <Eye className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeClosed className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </FormItem>
      </div>
      <FormItem name="school_id" vertical>
        <FormItem.Label>Escola</FormItem.Label>
        <FormItem.Control>
          <Combobox
            async={{
              url: '/public/schools',
              labelKey: 'name',
              valueKey: 'id'
            }}
            placeholder="Buscar escola..."
            emptyMessage="Nenhuma escola encontrada."
            className={inputClassName}
          />
        </FormItem.Control>
      </FormItem>
      <div className="grid gap-4 sm:grid-cols-2 items-start">
        <FormItem name="school_year" vertical>
          <FormItem.Label>Série</FormItem.Label>
          <FormItem.Control>
            <Combobox
              options={schoolYearOptions}
              placeholder="Selecione a série"
              emptyMessage="Nenhuma série encontrada."
              className={inputClassName}
            />
          </FormItem.Control>
        </FormItem>
        <FormItem name="birthdate" vertical>
          <FormItem.Label>Data de Nascimento</FormItem.Label>
          <FormItem.Control>
            <Input
              type="date"
              autoComplete="birthdate"
              className={inputClassName}
            />
          </FormItem.Control>
        </FormItem>
      </div>

      <FormItem name="accept_terms" vertical>
        <FormItem.Control>
          <FormCheck
            label={
              <>
                Li e concordo com os{' '}
                <AppLink
                  href="/termos-de-uso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary hover:text-primary/80"
                >
                  Termos de Uso
                </AppLink>{' '}
                e a{' '}
                <AppLink
                  href="/politica-de-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary hover:text-primary/80"
                >
                  Política de Privacidade
                </AppLink>{' '}
                desta plataforma, estando ciente de todas as condições e responsabilidades neles estabelecidas.
              </>
            }
          />
        </FormItem.Control>
      </FormItem>
    </>
  )
}
